import { MediaFileConnectionType, MediaFileUsedAs } from "@next-platform/packages-ts-types";
/**
 * The chatbots, personas and profile a file is attached to, named. A connection the response did not resolve
 * is left out rather than shown as its stored id: an id is not a name, and a missing card reads better than a
 * meaningless one. A chatbot is named from the embedded primary one; a persona or the owner's profile arrives
 * with its own name.
 */
function getNamedConnections(file) {
    if (!file || !("connections" in file) || !Array.isArray(file.connections)) {
        return [];
    }
    const primaryCharacter = file.character;
    const characterIds = new Set();
    return file.connections.flatMap((connection) => {
        if (connection.type === MediaFileConnectionType.CHARACTER) {
            if (characterIds.has(connection.id) || primaryCharacter?.id !== connection.id) {
                return [];
            }
            characterIds.add(connection.id);
            return [
                {
                    id: connection.id,
                    type: connection.type,
                    name: primaryCharacter.name,
                    ...(primaryCharacter.avatar_url ? { imageUrl: primaryCharacter.avatar_url } : {}),
                },
            ];
        }
        if (!connection.name) {
            return [];
        }
        return [
            { id: connection.id, type: connection.type, name: connection.name, ...(connection.avatar_url ? { imageUrl: connection.avatar_url } : {}) },
        ];
    });
}
function getPrimaryCharacterKind(usedAs) {
    if (usedAs.includes(MediaFileUsedAs.CHARACTER_AVATAR)) {
        return "avatar";
    }
    if (usedAs.includes(MediaFileUsedAs.CONVERSATION_IMAGE)) {
        return "conversation";
    }
    return "character";
}
function getConversationId(file) {
    return "conversation_id" in file ? file.conversation_id : undefined;
}
export default function buildMediaFileCharacters(file) {
    const character = file?.character;
    const kind = file ? getPrimaryCharacterKind(file.used_as) : undefined;
    const conversationId = file && kind === "conversation" ? getConversationId(file) : undefined;
    return {
        // The embed is already authorized for this caller and carries everything shown: name and avatar alike.
        primaryCharacter: character && kind
            ? {
                id: character.id,
                type: MediaFileConnectionType.CHARACTER,
                name: character.name,
                ...(character.avatar_url ? { imageUrl: character.avatar_url } : {}),
                kind,
                ...(conversationId ? { conversationId } : {}),
            }
            : undefined,
        connections: getNamedConnections(file),
    };
}
